// src/pages/DiscoverMode.jsx
// Self-contained Discover Mode — extracted from original App.jsx
// Handles: hero landing, gig search, artist search, gig results, date filter

import React, { useState, useEffect } from 'react';
import { Music, Eye } from 'lucide-react';
import FollowButton from '../components/FollowButton';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../contexts/AuthContext';

import {
  db,
  getUserLocation,
  searchNearbyGigs,
  calculateDistance,
  getArtistProfile,
  getFollowedArtistGigs,
} from '../firebase-utils';

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
} from 'firebase/firestore';

// ============================================
// SOCIAL FOLLOW BUTTONS
// ============================================
const SocialFollowButtons = ({ socialMedia, artistName }) => {
  if (!socialMedia || !Object.values(socialMedia).some(v => v)) return null;

  const getSocialUrl = (platform, value) => {
    if (!value) return null;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    switch (platform) {
      case 'instagram': return `https://instagram.com/${value.replace('@', '')}`;
      case 'facebook': return value.includes('facebook.com') ? value : `https://facebook.com/${value}`;
      case 'twitter': return `https://twitter.com/${value.replace('@', '')}`;
      case 'youtube': return value.includes('youtube.com') ? value : `https://youtube.com/${value}`;
      case 'spotify': return value.includes('spotify.com') ? value : `https://open.spotify.com/artist/${value}`;
      case 'tiktok': return `https://tiktok.com/@${value.replace('@', '')}`;
      case 'soundcloud': return `https://soundcloud.com/${value}`;
      default: return value;
    }
  };

  const platforms = [
    { key: 'instagram', label: '📸 Instagram', color: '#E1306C' },
    { key: 'facebook', label: '👍 Facebook', color: '#1877F2' },
    { key: 'youtube', label: '▶️ YouTube', color: '#FF0000' },
    { key: 'spotify', label: '🎵 Spotify', color: '#1DB954' },
    { key: 'twitter', label: '🐦 Twitter', color: '#1DA1F2' },
    { key: 'tiktok', label: '🎵 TikTok', color: '#000000' },
    { key: 'soundcloud', label: '☁️ SoundCloud', color: '#FF5500' },
  ];

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {platforms.map(({ key, label, color }) => {
        const value = socialMedia[key];
        if (!value) return null;
        const url = getSocialUrl(key, value);
        if (!url) return null;
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer"
            className="social-btn text-xs" style={{ borderColor: color, color }}>
            {label}
          </a>
        );
      })}
    </div>
  );
};

// ============================================
// CALCULATE GIG STATUS
// ============================================
const calculateGigStatus = (gig) => {
  const now = new Date();
  const dateStr = gig.gigDate || gig.date || '';
  const timeStr = gig.gigTime || gig.time || '00:00';
  if (!dateStr) return 'upcoming';
  const gigTime = new Date(`${dateStr}T${timeStr}`);
  const endTime = new Date(gigTime.getTime() + (gig.duration || 180) * 60000);
  if (gig.status === 'cancelled') return 'cancelled';
  if (gig.status === 'ended') return 'ended';
  if (now >= gigTime && now <= endTime) return 'live';
  if (now > endTime) return 'ended';
  return 'upcoming';
};

// ============================================
// MAIN DISCOVER MODE COMPONENT
// ============================================
export default function DiscoverMode({
  onModeChange,
  setLiveGig,
  setShowAuthModal,
  setSelectedUpcomingGig,
  setShowGigDetailModal,
  setUserVotes,
  setHasRatedArtist,
  interestedGigs,
  setInterestedGigs,
  onVenuePortal,
  onJoinFromNotification,
}) {
  const { currentUser } = useAuth();

  // ── Core state ──
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyGigs, setNearbyGigs] = useState([]);
  const [filteredGigs, setFilteredGigs] = useState([]);
  const [loadingGigs, setLoadingGigs] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');

  // ── Followed artists feed state ──
  const [followedGigs, setFollowedGigs] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedLoaded, setFeedLoaded] = useState(false);

  // ── Artist search state ──
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSuggestions, setArtistSuggestions] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [showArtistSuggestions, setShowArtistSuggestions] = useState(false);
  const [showingArtistResults, setShowingArtistResults] = useState(false);

  // ── Load followed artist gigs when user logs in ──
  useEffect(() => {
    if (!currentUser || feedLoaded) return;
    setLoadingFeed(true);
    getFollowedArtistGigs(currentUser.uid)
      .then(gigs => { setFollowedGigs(gigs); setFeedLoaded(true); })
      .catch(console.error)
      .finally(() => setLoadingFeed(false));
  }, [currentUser]);

  // ============================================
  // SEARCH GIGS
  // ============================================
  const handleSearchGigs = async () => {
    try {
      setLoadingGigs(true);
      let location = userLocation;

      if (!location) {
        try {
          location = await getUserLocation();
          setUserLocation(location);
        } catch (locError) {
          setLoadingGigs(false);
          if (locError.code === 1) {
            alert('📍 Location Access Required\n\nPlease enable location access to find nearby gigs:\n\n1. Click the location icon 🔒 in your browser address bar\n2. Change "Location" to "Allow"\n3. Refresh the page and try again');
          } else if (locError.code === 2) {
            alert('📍 Location Unavailable\n\nCould not determine your location.\n\nPlease check that location services are enabled on your device.');
          } else if (locError.code === 3) {
            alert('📍 Location request timed out. Please try again.');
          } else {
            alert('📍 Could not get your location. Please enable location services and try again.');
          }
          return;
        }
      }

      const gigs = await searchNearbyGigs(location, 50);

      const activeGigs = gigs.filter(gig => {
        const status = calculateGigStatus(gig);
        return ['live', 'checkVenue', 'upcoming'].includes(status);
      });

      setNearbyGigs(gigs);
      setFilteredGigs(gigs);

      if (activeGigs.length === 0) {
        alert('No active gigs found within 50km. Try again later!');
      } else {
        alert(`✅ Found ${activeGigs.length} active gig${activeGigs.length > 1 ? 's' : ''} nearby!`);
      }
    } catch (error) {
      console.error('Error searching gigs:', error);
      alert('Error finding gigs: ' + error.message);
    } finally {
      setLoadingGigs(false);
    }
  };

  // ============================================
  // DATE FILTER
  // ============================================
  const filterGigsByDate = (filter) => {
    setDateFilter(filter);

    if (filter === 'all') {
      setFilteredGigs(nearbyGigs.filter(g => g.status !== 'ended' && g.status !== 'cancelled'));
      return;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const activeGigs = nearbyGigs.filter(gig =>
      gig.status !== 'ended' && gig.status !== 'cancelled'
    );

    const filtered = activeGigs.filter(gig => {
      if (!gig.gigDate) return true;
      const gigDate = new Date(gig.gigDate);
      const gigDay = new Date(gigDate.getFullYear(), gigDate.getMonth(), gigDate.getDate());

      if (filter === 'today') return gigDay.getTime() === today.getTime();
      if (filter === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return gigDay.getTime() === tomorrow.getTime();
      }
      if (filter === 'week') {
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return gigDay >= today && gigDay <= weekEnd;
      }
      return true;
    });

    setFilteredGigs(filtered);
  };

  // ============================================
  // SEARCH FILTER
  // ============================================
  const filterGigsBySearch = (gigsToFilter) => {
    if (!searchTerm.trim()) return gigsToFilter;
    const search = searchTerm.toLowerCase().trim();
    return gigsToFilter.filter(gig => {
      const venueName = (gig.venueName || '').toLowerCase();
      const artistName = (gig.artistName || '').toLowerCase();
      const venueAddress = (gig.venueAddress || '').toLowerCase();
      return venueName.includes(search) || artistName.includes(search) || venueAddress.includes(search);
    });
  };

  // ============================================
  // ARTIST SEARCH
  // ============================================
  const handleArtistSearch = async (term) => {
    setArtistSearchQuery(term);

    if (!term || term.trim().length < 2) {
      setArtistSuggestions([]);
      setShowArtistSuggestions(false);
      setShowingArtistResults(false);
      return;
    }

    try {
      setLoadingArtists(true);
      setShowingArtistResults(true);

      const searchLower = term.toLowerCase().trim();
      const artists = [];

      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);

      for (const userDoc of usersSnapshot.docs) {
        try {
          const profileRef = doc(db, 'users', userDoc.id, 'profile', 'data');
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            const profile = profileSnap.data();
            if (profile.artistName) {
              const artistName = profile.artistName.toLowerCase();
              const fullName = (profile.fullName || '').toLowerCase();
              if (artistName.includes(searchLower) || fullName.includes(searchLower)) {
                artists.push({
                  id: userDoc.id,
                  artistName: profile.artistName,
                  fullName: profile.fullName || '',
                  genre: profile.genre || '',
                  location: profile.location || '',
                  profilePhoto: profile.profilePhoto || '',
                  bio: profile.bio || '',
                  socialMedia: profile.socialMedia || {}
                });
              }
            }
          }
        } catch (profileError) {
          // Skip users without profiles
        }
      }

      artists.sort((a, b) => {
        const aName = a.artistName.toLowerCase();
        const bName = b.artistName.toLowerCase();
        if (aName === searchLower) return -1;
        if (bName === searchLower) return 1;
        if (aName.startsWith(searchLower) && !bName.startsWith(searchLower)) return -1;
        if (bName.startsWith(searchLower) && !aName.startsWith(searchLower)) return 1;
        return aName.localeCompare(bName);
      });

      setArtistSuggestions(artists);
      setShowArtistSuggestions(true);

    } catch (error) {
      console.error('Search error:', error);
      if (error.code === 'permission-denied') {
        alert('⚠️ Firebase rules need updating\n\nMake sure Firebase rules allow public read access to users collection.');
      } else {
        alert('Error searching artists: ' + error.message);
      }
    } finally {
      setLoadingArtists(false);
    }
  };

  // ============================================
  // VIEW ARTIST PROFILE
  // ============================================
  const handleViewArtistProfile = async (artistId) => {
    if (!currentUser) {
      alert('🔐 Please sign in to view artist details and gigs');
      setShowAuthModal(true);
      return;
    }

    try {
      const profile = await getArtistProfile(artistId);
      if (!profile) { alert('❌ Artist profile not found'); return; }

      const gigsQuery = query(
        collection(db, 'liveGigs'),
        where('artistId', '==', artistId),
        where('status', 'in', ['upcoming', 'live']),
        orderBy('gigDate', 'asc'),
        limit(10)
      );
      const gigsSnapshot = await getDocs(gigsQuery);
      const artistGigs = gigsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      onModeChange('artistProfile', { profile, gigs: artistGigs, artistId });
    } catch (error) {
      console.error('Error loading artist profile:', error);
      alert('❌ Error loading profile');
    }
  };

  // ============================================
  // JOIN LIVE GIG
  // ============================================
  const checkIfUserRatedArtist = async (artistId, userId) => {
    try {
      const gigsRef = collection(db, 'liveGigs');
      const q = query(gigsRef, where('artistId', '==', artistId));
      const snapshot = await getDocs(q);
      for (const gigDoc of snapshot.docs) {
        const gigData = gigDoc.data();
        if (gigData.ratings?.some(r => r.userId === userId)) return true;
      }
      return false;
    } catch (error) { return false; }
  };

  const getUserVotesForGig = async (gigId, userId) => {
    try {
      const votesRef = collection(db, 'liveGigs', String(gigId), 'userVotes');
      const q = query(votesRef, where('userId', '==', userId));
      const snapshot = await getDocs(q);
      const votes = {};
      snapshot.forEach(d => { votes[d.data().songId] = true; });
      return votes;
    } catch (error) { return {}; }
  };

  const handleJoinGig = async (gig) => {
    try {
      if (!currentUser) {
        alert('🔐 Please sign in to join this live gig!');
        setShowAuthModal(true);
        return;
      }

      let location = userLocation;
      if (!location) {
        alert('📍 Getting your location...\n\nPlease allow location access when prompted.');
        try {
          location = await getUserLocation();
          setUserLocation(location);
        } catch (locError) {
          alert('❌ Could not get your location.\n\nPlease enable location services in your browser settings.\n\nError: ' + locError.message);
          return;
        }
      }

      if (!gig.location?.latitude || !gig.location?.longitude) {
        alert('⚠️ This gig does not have a valid location.\n\nPlease contact the artist.');
        return;
      }

      const venueLocation = { lat: gig.location.latitude, lng: gig.location.longitude };
      const distance = calculateDistance(location, venueLocation);
      const maxDistance = 1000;

      if (distance > maxDistance) {
        const distanceKm = (distance / 1000).toFixed(1);
        alert(`⚠️ You're too far away!\n\nYou must be within 1km of the venue to join.\n\nYou are currently ${distanceKm}km away from ${gig.venueName}.\n\n📍 Venue: ${gig.venueAddress || gig.venueName}`);
        return;
      }

      setLiveGig(gig);
      onModeChange('audience');

      if (currentUser) {
        localStorage.setItem(`GigWave_live_${currentUser.uid}`, JSON.stringify({ ...gig, _role: 'audience' }));

        try {
          const existingVotes = await getUserVotesForGig(gig.id, currentUser.uid);
          setUserVotes(existingVotes);
        } catch (error) { /* Non-blocking */ }
      }

      try {
        const hasRated = await checkIfUserRatedArtist(gig.artistId, currentUser.uid);
        setHasRatedArtist(hasRated);
      } catch (error) { /* Non-blocking */ }

      try {
        const gigRef = doc(db, 'liveGigs', String(gig.id));
        const gigSnap = await getDoc(gigRef);
        if (gigSnap.exists()) {
          const currentTracking = gigSnap.data().audienceTracking || {
            totalJoins: 0, currentlyActive: 0, joinedUsers: {}
          };
          const userId = currentUser.uid;
          const isFirstJoin = !currentTracking.joinedUsers[userId];
          currentTracking.joinedUsers[userId] = {
            firstJoin: currentTracking.joinedUsers[userId]?.firstJoin || new Date().toISOString(),
            lastActive: new Date().toISOString(),
            isActive: true,
            userName: currentUser.displayName || 'Anonymous'
          };
          if (isFirstJoin) currentTracking.totalJoins = (currentTracking.totalJoins || 0) + 1;
          currentTracking.currentlyActive = Object.values(currentTracking.joinedUsers).filter(u => u.isActive).length;
          await updateDoc(gigRef, { audienceTracking: currentTracking });
        }
      } catch (trackError) { /* Non-blocking */ }

      alert(`✅ Joined ${gig.artistName}'s live gig at ${gig.venueName}!`);
    } catch (error) {
      alert('❌ Error joining gig: ' + error.message);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="rock-background min-h-screen p-4 pb-24 md:pb-4">

      {/* ── Notification Bell — fixed top-right ── */}
      {currentUser && (
        <div className="fixed top-4 right-4 z-50">
          <NotificationBell onJoinLive={onJoinFromNotification} />
        </div>
      )}

      <div className="max-w-4xl mx-auto pt-8 md:pt-20">

        {/* ── Hero Section ── */}
        <div className="text-center mb-8 md:mb-12">

          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative logo-icon" data-testid="logo">
              <svg width="80" height="80" viewBox="0 0 100 100">
                <rect x="10" y="30" width="8" height="40" fill="#00FFD4" rx="4">
                  <animate attributeName="height" values="40;60;40" dur="1s" repeatCount="indefinite" />
                  <animate attributeName="y" values="30;20;30" dur="1s" repeatCount="indefinite" />
                </rect>
                <rect x="25" y="20" width="8" height="60" fill="#00D4FF" rx="4">
                  <animate attributeName="height" values="60;80;60" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y" values="20;10;20" dur="1.2s" repeatCount="indefinite" />
                </rect>
                <rect x="40" y="15" width="8" height="70" fill="#FF1B6D" rx="4">
                  <animate attributeName="height" values="70;90;70" dur="0.8s" repeatCount="indefinite" />
                  <animate attributeName="y" values="15;5;15" dur="0.8s" repeatCount="indefinite" />
                </rect>
                <rect x="55" y="20" width="8" height="60" fill="#9D00FF" rx="4">
                  <animate attributeName="height" values="60;80;60" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y" values="20;10;20" dur="1.2s" repeatCount="indefinite" />
                </rect>
                <rect x="70" y="30" width="8" height="40" fill="#00FFD4" rx="4">
                  <animate attributeName="height" values="40;60;40" dur="1s" repeatCount="indefinite" />
                  <animate attributeName="y" values="30;20;30" dur="1s" repeatCount="indefinite" />
                </rect>
              </svg>
            </div>
            <h1 className="gigwave-logo" data-text="GigWave">GigWave</h1>
          </div>

          {nearbyGigs.length === 0 && (
            <>
              <p className="hero-tagline">
                {['Ride', 'The', 'Wave', 'Of', 'Live', 'Music'].map((word, i) => (
                  <span key={i} style={{ marginRight: '0.3em' }}>{word}</span>
                ))}
              </p>
              <div className="lightning-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <svg className="lightning-bolt left" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                </svg>
                <h2 className="discover-text">Discover Live Music Near You</h2>
                <svg className="lightning-bolt right" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                </svg>
              </div>
            </>
          )}
        </div>

        {/* ── Followed Artists Feed ── */}
        {currentUser && nearbyGigs.length === 0 && (followedGigs.length > 0 || loadingFeed) && (
          <div className="mb-8">
            <div className="gig-card p-5">
              <h3 className="text-electric font-bold text-sm uppercase tracking-wider mb-4">
                🔔 Gigs From Artists You Follow
              </h3>

              {loadingFeed && (
                <div className="flex items-center gap-3 py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-electric flex-shrink-0"></div>
                  <p className="text-gray-400 text-sm">Loading your feed...</p>
                </div>
              )}

              {!loadingFeed && followedGigs.length === 0 && (
                <p className="text-gray-500 text-sm">No upcoming gigs from artists you follow yet.</p>
              )}

              {!loadingFeed && followedGigs.map(gig => {
                const isLive = gig.status === 'live';
                return (
                  <div key={gig.id}
                    className="flex items-center justify-between gap-4 py-3 border-b border-white/10 last:border-b-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold truncate">{gig.artistName}</span>
                        {isLive && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full animate-pulse"
                            style={{ background: 'rgba(57,255,20,0.15)', color: '#39FF14', border: '1px solid rgba(57,255,20,0.4)' }}>
                            🟢 LIVE NOW
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5">
                        📍 {gig.venueName}
                        {gig.gigDate ? ` · ${new Date(gig.gigDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                        {gig.gigTime ? ` at ${gig.gigTime}` : ''}
                      </p>
                    </div>
                    {isLive ? (
                      <button
                        onClick={() => {
                          if (!currentUser) { setShowAuthModal(true); return; }
                          setLiveGig(gig);
                          onModeChange('audience');
                        }}
                        className="btn btn-neon text-xs whitespace-nowrap flex-shrink-0">
                        🎵 Join Live
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            const gigSnap = await getDoc(doc(db, 'liveGigs', String(gig.id)));
                            const fullGig = gigSnap.exists() ? { id: gigSnap.id, ...gigSnap.data() } : gig;
                            setSelectedUpcomingGig(fullGig);
                            setShowGigDetailModal(true);
                          } catch { setSelectedUpcomingGig(gig); setShowGigDetailModal(true); }
                        }}
                        className="btn btn-electric text-xs whitespace-nowrap flex-shrink-0">
                        🎵 View
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Find Gigs Button ── */}
        {nearbyGigs.length === 0 && (
          <div className="mb-8">
            <button
              data-testid="find-gigs-btn"
              onClick={handleSearchGigs}
              disabled={loadingGigs}
              className="btn btn-electric w-full text-xl md:text-2xl py-6 md:py-8 hover:scale-105 transition-transform"
            >
              {loadingGigs ? (
                <><span className="loading-pulse">🔍</span><span>Searching...</span></>
              ) : (
                <><span>📍</span><span>Find Live Gigs</span></>
              )}
            </button>
          </div>
        )}

        {/* ── Artist Search ── */}
        {nearbyGigs.length === 0 && (
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                data-testid="artist-search"
                value={artistSearchQuery}
                onChange={(e) => handleArtistSearch(e.target.value)}
                onFocus={() => artistSuggestions.length > 0 && setShowArtistSuggestions(true)}
                placeholder="Search by artist name..."
                className="w-full px-4 py-4 pr-12 rounded-lg bg-white/10 text-white border-2 border-magenta/30 focus:border-magenta focus:outline-none text-lg"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-magenta">
                {loadingArtists ? <div className="animate-spin">⏳</div> : <span>🔍</span>}
              </div>

              {showArtistSuggestions && artistSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-2 bg-gray-900 border-2 border-magenta rounded-lg shadow-2xl max-h-80 overflow-y-auto">
                  {artistSuggestions.map(artist => (
                    <div
                      key={artist.id}
                      data-testid="artist-result-item"
                      className="p-4 hover:bg-magenta/20 border-b border-white/10 last:border-b-0 transition"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-magenta to-electric flex items-center justify-center flex-shrink-0">
                          <Music size={24} className="text-white" />
                        </div>
                        <button
                          className="flex-1 min-w-0 text-left"
                          onClick={() => {
                            handleViewArtistProfile(artist.id);
                            setShowArtistSuggestions(false);
                            setArtistSearchQuery('');
                          }}>
                          <div className="text-white font-bold text-lg truncate">{artist.artistName}</div>
                          <div className="flex flex-wrap gap-2 text-sm">
                            {artist.genre && <span className="text-electric">🎵 {artist.genre}</span>}
                            {artist.location && <span className="text-gray-light">📍 {artist.location}</span>}
                          </div>
                        </button>
                        <FollowButton
                          artistId={artist.id}
                          artistName={artist.artistName}
                          size="sm"
                          onAuthRequired={() => { setShowArtistSuggestions(false); setShowAuthModal(true); }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showArtistSuggestions && artistSuggestions.length === 0 && artistSearchQuery.length >= 2 && !loadingArtists && (
                <div className="absolute z-50 w-full mt-2 bg-gray-900 border-2 border-magenta rounded-lg p-6 text-center">
                  <p className="text-gray-light">No artists found for "{artistSearchQuery}"</p>
                  <p className="text-gray text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {showArtistSuggestions && (
                <div className="fixed inset-0 z-40" onClick={() => setShowArtistSuggestions(false)} />
              )}
            </div>
          </div>
        )}

        {/* ══════════════ GIG RESULTS ══════════════ */}
        {nearbyGigs.length > 0 && (
          <div className="space-y-6">

            {/* Search box */}
            <div className="mb-6">
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by venue, artist, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%', padding: '14px 50px 14px 16px',
                    background: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px', color: 'white', fontSize: '16px',
                    fontWeight: '600', transition: 'all 0.3s ease', outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(0, 212, 255, 0.6)';
                    e.target.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#00d4ff', fontSize: '20px', pointerEvents: 'none' }}>
                  🔍
                </span>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute', right: '50px', top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(255, 107, 53, 0.2)', border: '2px solid rgba(255, 107, 53, 0.5)',
                      borderRadius: '50%', width: '28px', height: '28px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: '#ff6b35',
                      fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.3s ease', padding: '0'
                    }}
                  >✕</button>
                )}
              </div>
              {searchTerm && (
                <div style={{ marginTop: '12px', color: '#00d4ff', fontSize: '14px', fontWeight: '600' }}>
                  📊 Found {filterGigsBySearch(filteredGigs).filter(g => ['live','checkVenue','upcoming'].includes(calculateGigStatus(g))).length} gigs matching "{searchTerm}"
                </div>
              )}
            </div>

            {/* Date filter dropdown */}
            <div className="flex items-center gap-4 mb-4">
              <label htmlFor="dateFilter" className="text-electric font-bold text-base whitespace-nowrap">
                🗓️ Filter:
              </label>
              <select
                id="dateFilter"
                value={dateFilter}
                onChange={(e) => filterGigsByDate(e.target.value)}
                className="px-3 py-2 rounded-lg bg-white/10 text-white border-2 border-electric/30 focus:border-electric focus:outline-none text-base font-bold cursor-pointer"
              >
                <option value="today" className="bg-gray-900 text-white font-bold">Today</option>
                <option value="tomorrow" className="bg-gray-900 text-white font-bold">Tomorrow</option>
                <option value="week" className="bg-gray-900 text-white font-bold">This Week</option>
                <option value="all" className="bg-gray-900 text-white font-bold">All Dates</option>
              </select>
            </div>

            {/* Results counter */}
            <p className="text-gray-light text-sm mb-6 text-center">
              🎵 Showing{' '}
              <span className="text-electric font-bold">
                {filteredGigs.filter(g => ['live', 'checkVenue', 'upcoming'].includes(calculateGigStatus(g))).length}
              </span>{' '}
              of{' '}
              <span className="text-electric font-bold">
                {nearbyGigs.filter(g => ['live', 'checkVenue', 'upcoming'].includes(calculateGigStatus(g))).length}
              </span>{' '}
              active gigs
            </p>

            {/* Gig cards */}
            <div className="space-y-4">
              {(() => {
                const searchedGigs = filterGigsBySearch(filteredGigs);
                const displayGigs = searchedGigs
                  .map(gig => ({ ...gig, calculatedStatus: calculateGigStatus(gig) }))
                  .filter(gig => ['live', 'checkVenue', 'upcoming'].includes(gig.calculatedStatus))
                  .sort((a, b) => {
                    const order = { live: 0, checkVenue: 1, upcoming: 2 };
                    if (a.calculatedStatus !== b.calculatedStatus)
                      return order[a.calculatedStatus] - order[b.calculatedStatus];
                    return new Date(`${a.gigDate}T${a.gigTime}`) - new Date(`${b.gigDate}T${b.gigTime}`);
                  });

                if (displayGigs.length === 0) {
                  return (
                    <div className="bg-yellow-500/20 border border-yellow-400 rounded-xl p-6 text-center">
                      <p className="text-yellow-200 text-lg">
                        {searchTerm ? `No gigs found matching "${searchTerm}"` : 'No active gigs found for selected date filter.'}
                      </p>
                      {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="btn btn-electric mt-4">
                          Clear Search
                        </button>
                      )}
                    </div>
                  );
                }

                const liveGigs = displayGigs.filter(g => g.calculatedStatus === 'live');
                const upcomingGigs = displayGigs.filter(g => g.calculatedStatus === 'upcoming' || g.calculatedStatus === 'checkVenue');

                return (
                  <>
                    {/* ── LIVE GIGS ── */}
                    {liveGigs.map(gig => (
                      <div key={gig.id} className="gig-card gig-card-live hover:scale-[1.02] transition-all">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-1 mb-1">
                              <span className="concert-heading text-xs tracking-wider" style={{ color: '#39FF14' }}>
                                <span className="inline-block animate-pulse" style={{
                                  filter: 'drop-shadow(0 0 8px #39FF14) drop-shadow(0 0 16px #39FF14)',
                                  textShadow: '0 0 10px #39FF14, 0 0 20px #39FF14'
                                }}>🟢</span> LIVE NOW
                              </span>
                            </div>
                            <h3 className="concert-heading text-base text-white mb-0.5">{gig.artistName}</h3>
                            <div className="space-y-2 text-gray-light">
                              <p className="flex items-center gap-2 text-sm">
                                <span className="text-electric">📍</span>
                                <span className="font-semibold">{gig.venueName}</span>
                                {gig.distance != null && !isNaN(gig.distance) && (
                                  <span className="text-electric font-bold">
                                    • {gig.distance < 1000
                                      ? `${Math.round(gig.distance)}m away`
                                      : `${(gig.distance / 1000).toFixed(1)}km away`}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          {gig.artistProfile?.socialMedia && (
                            <div className="mt-4 pt-4 border-t border-white/20">
                              <SocialFollowButtons socialMedia={gig.artistProfile.socialMedia} artistName={gig.artistName} />
                            </div>
                          )}

                          <div className="flex flex-col gap-3">
                            <button
                              onClick={async (e) => {
                                e.preventDefault(); e.stopPropagation();
                                try {
                                  const gigRef = doc(db, 'liveGigs', gig.id);
                                  const gigSnap = await getDoc(gigRef);
                                  const fullGigData = gigSnap.exists()
                                    ? { id: gigSnap.id, ...gigSnap.data(), distance: gig.distance }
                                    : gig;
                                  setSelectedUpcomingGig(fullGigData);
                                  setShowGigDetailModal(true);
                                } catch (error) {
                                  setSelectedUpcomingGig(gig);
                                  setShowGigDetailModal(true);
                                }
                              }}
                              className="btn btn-electric text-lg md:text-xl whitespace-nowrap touch-target"
                            >
                              <Eye size={20} />
                              <span>VIEW PLAYLIST</span>
                            </button>

                            <button
                              onClick={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                if (!currentUser) {
                                  alert('🔐 Sign in to join and interact with this live gig!');
                                  setShowAuthModal(true);
                                  return;
                                }
                                handleJoinGig(gig);
                              }}
                              className="btn btn-neon text-lg md:text-xl whitespace-nowrap touch-target"
                            >
                              <span>🎵</span>
                              <span>JOIN LIVE</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* ── UPCOMING GIGS ── */}
                    {upcomingGigs.length > 0 && (
                      <div className="space-y-4">
                        <h2 className="concert-heading text-4xl text-electric mb-6">📅 UPCOMING GIGS</h2>
                        {upcomingGigs.map(gig => {
                          const gigDateTime = gig.gigDate && gig.gigTime
                            ? `${new Date(gig.gigDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${gig.gigTime}`
                            : 'Time TBD';
                          return (
                            <div key={gig.id} className="gig-card hover:scale-[1.02] transition-all">
                              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className={`w-2 h-2 rounded-full ${gig.calculatedStatus === 'checkVenue' ? 'bg-orange-500' : 'bg-electric'}`}></div>
                                    <span className={`font-bold text-xs uppercase tracking-wider ${gig.calculatedStatus === 'checkVenue' ? 'text-orange-400' : 'text-electric'}`}>
                                      {gig.calculatedStatus === 'checkVenue' ? 'Check With Venue' : 'Upcoming'}
                                    </span>
                                  </div>
                                  <h3 className="concert-heading text-xl md:text-2xl text-white mb-2">{gig.artistName}</h3>
                                  <p className="text-magenta font-bold text-base mb-3">🎸 {gigDateTime}</p>
                                  <div className="space-y-1 text-gray-light">
                                    <p className="flex items-center gap-2 text-sm">
                                      <span className="text-electric">📍</span>
                                      <span className="font-semibold">{gig.venueName}</span>
                                      {gig.distance != null && !isNaN(gig.distance) && (
                                        <span className="text-electric font-bold">
                                          • {gig.distance < 1000
                                            ? `${Math.round(gig.distance)}m away`
                                            : `${(gig.distance / 1000).toFixed(1)}km away`}
                                        </span>
                                      )}
                                    </p>
                                    {interestedGigs?.includes(gig.id) && (
                                      <p className="flex items-center gap-2 text-neon font-semibold mt-2 text-sm">
                                        <span>⭐</span><span>You're interested!</span>
                                      </p>
                                    )}
                                    {gig.interestedCount > 0 && (
                                      <p className="flex items-center gap-2 text-magenta font-semibold text-sm">
                                        <span>👥</span>
                                        <span>{gig.interestedCount} {gig.interestedCount === 1 ? 'person' : 'people'} interested</span>
                                      </p>
                                    )}
                                  </div>
                                </div>

                                <button
                                  onClick={async () => {
                                    try {
                                      const gigRef = doc(db, 'liveGigs', gig.id);
                                      const gigSnap = await getDoc(gigRef);
                                      const fullGigData = gigSnap.exists()
                                        ? { id: gigSnap.id, ...gigSnap.data(), distance: gig.distance }
                                        : gig;
                                      setSelectedUpcomingGig(fullGigData);
                                      setShowGigDetailModal(true);
                                    } catch (error) {
                                      setSelectedUpcomingGig(gig);
                                      setShowGigDetailModal(true);
                                    }
                                  }}
                                  className="btn btn-electric text-sm md:text-base px-4 py-2 whitespace-nowrap self-center touch-target"
                                >
                                  <span>🎵</span><span>VIEW PLAYLIST</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Bottom buttons ── */}
        {nearbyGigs.length === 0 && !showingArtistResults && (
          <div className="mt-8 text-center space-y-4">
            {currentUser ? (
              <>
                <p data-testid="user-greeting" className="text-white mb-4">Signed in as: {currentUser.email}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    data-testid="artist-mode-btn"
                    onClick={() => onModeChange('artist')}
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-bold text-xl"
                  >
                    🎤 Go to Artist Mode
                  </button>
                  <button
                    onClick={onVenuePortal}
                    className="flex-1 px-8 py-4 text-white rounded-xl font-bold text-xl"
                    style={{
                      background: 'linear-gradient(135deg, #1e3a5f, #1d4ed8)',
                      border: '1px solid rgba(59,130,246,0.5)',
                      boxShadow: '0 4px 20px rgba(29,78,216,0.4)',
                    }}
                  >
                    🏢 Venue Portal
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  data-testid="auth-trigger"
                  onClick={() => setShowAuthModal(true)}
                  className="flex-1 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-bold text-xl"
                >
                  🎤 Artist Sign In
                </button>
                <button
                  onClick={onVenuePortal}
                  className="flex-1 px-8 py-4 text-white rounded-xl font-bold text-xl"
                  style={{
                    background: 'linear-gradient(135deg, #1e3a5f, #1d4ed8)',
                    border: '1px solid rgba(59,130,246,0.5)',
                    boxShadow: '0 4px 20px rgba(29,78,216,0.4)',
                  }}
                >
                  🏢 Venue Portal
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="mt-6 pt-4 border-t border-white/20 text-center">
          <p className="text-gray-300 text-sm mb-1">Questions or feedback?</p>
          <a
            href="mailto:gig.wave.2005@gmail.com"
            className="text-cyan-400 hover:text-cyan-300 transition-colors text-base font-semibold"
          >
            📧 gig.wave.2005@gmail.com
          </a>
        </div>

      </div>
    </div>
  );
}
